import { useRef, useState, Dispatch, SetStateAction, useContext } from 'react'

import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    makeStyles,
    TextField,
    Typography
} from '@material-ui/core'

import { SpacedGroup, VideoStatusContext } from 'UI/Components'
import { Download } from 'UI/Routes/main/common'

import { NotAvailableMessage } from './NotAvailableMessage'

import { useNotification, createEmbedCode } from 'hooks'
import { copyToClipboard } from 'modules'

import { PlaybookType } from 'app/types'

const useStyles = makeStyles(theme => ({
    title: {
        textAlign: 'center',
        fontWeight: theme.fontWeightBold
    },
    closeButton: {
        position: 'absolute',
        right: 0,
        top: 0,
        padding: theme.spacing(0.5),
        margin: theme.spacing(0.5)
    },
    code: {
        padding: theme.spacing(1),
        background: '#fff',
        borderRadius: '4px'
    },
    actions: {
        padding: theme.spacing(3),
        justifyContent: 'center'
    }
}))

const iframeType = {
    responsive: 'Responsive',
    fixed: 'Fixed'
}
type Props = {
    playbook: PlaybookType
    hasVideoExport: boolean
}

export const ExportTab = ({ playbook, hasVideoExport }: Props) => {
    const classes = useStyles()
    const { showSuccessNotification } = useNotification()

    const { processFinished, isLoading } = useContext(VideoStatusContext)

    const codeRef = useRef<HTMLElement | null>(null)

    const [type, setType] = useState(iframeType.fixed)
    const [width, setWidth] = useState(700)
    const [height, setHeight] = useState(400)

    const handleSizeChange = (event: any, callback: Dispatch<SetStateAction<number>>) => {
        const onlyNums = event.target.value.replace(/[^0-9]/g, '')
        if (onlyNums.length < 5) callback(onlyNums)
    }

    const iframeWidth = type === iframeType.responsive ? '100%' : width + 'px'
    const iframeHeight = type === iframeType.responsive ? '575px' : height + 'px'

    const isLocked = !playbook.isMainIncluded && !playbook.isPublic

    return (
        <Box mt={2} position="relative">
            <Box width="100%">
                <Typography
                    style={{
                        fontWeight: 700
                    }}
                >
                    Downloads
                </Typography>
                <Box mt={2} />
                {playbook && (
                    <Download
                        playbook={playbook}
                        playbookId={playbook.id}
                        fileName={playbook.title}
                        mode={playbook.mode}
                        disableMP4={!processFinished || isLoading}
                    />
                )}
            </Box>

            <Box position="relative">
                {(isLocked || !hasVideoExport) && (
                    <NotAvailableMessage
                        style={{ marginTop: '-15px' }}
                        {...(!hasVideoExport && {
                            message: 'Options Available on the Business Plan'
                        })}
                    />
                )}

                <Box mt={2} />
                <Typography
                    style={{
                        fontWeight: 700
                    }}
                >
                    Embed
                </Typography>

                <Box className={classes.code} mt={2}>
                    <code ref={codeRef}>
                        {createEmbedCode({ iframeWidth, iframeHeight, playbook })}
                    </code>
                </Box>

                <Box
                    mt={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    borderTop="1px solid #e2e2e3"
                    py={2}
                >
                    <SpacedGroup>
                        <FormControlLabel
                            value={iframeType.responsive}
                            onClick={() => setType(iframeType.responsive)}
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={type === iframeType.responsive}
                                />
                            }
                            label={
                                <Typography
                                    style={{ fontSize: '14px' }}
                                    color={
                                        type === iframeType.responsive ? 'inherit' : 'textSecondary'
                                    }
                                >
                                    {iframeType.responsive}
                                </Typography>
                            }
                        />
                        <SpacedGroup>
                            <FormControlLabel
                                value={iframeType.fixed}
                                onClick={() => setType(iframeType.fixed)}
                                control={
                                    <Checkbox color="primary" checked={type === iframeType.fixed} />
                                }
                                label={
                                    <Typography
                                        style={{ fontSize: '14px' }}
                                        color={
                                            type === iframeType.fixed ? 'inherit' : 'textSecondary'
                                        }
                                    >
                                        {iframeType.fixed}
                                    </Typography>
                                }
                            />
                            {type === iframeType.fixed && (
                                <SpacedGroup spacing={1}>
                                    <TextField
                                        value={width}
                                        onChange={e => handleSizeChange(e, setWidth)}
                                        variant="filled"
                                        size="small"
                                        inputProps={{
                                            style: inputStyles
                                        }}
                                    />
                                    <Typography style={{ fontSize: '14px' }} color="textSecondary">
                                        -
                                    </Typography>
                                    <TextField
                                        value={height}
                                        onChange={e => handleSizeChange(e, setHeight)}
                                        variant="filled"
                                        size="small"
                                        inputProps={{
                                            style: inputStyles
                                        }}
                                    />
                                    <Typography style={{ fontSize: '14px' }} color="textSecondary">
                                        px
                                    </Typography>
                                </SpacedGroup>
                            )}
                        </SpacedGroup>
                    </SpacedGroup>

                    <Button
                        onClick={() => {
                            copyToClipboard(codeRef?.current?.innerText).then(() => {
                                showSuccessNotification('Code was copied to the clipboard')
                            })
                        }}
                    >
                        COPY
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}

const inputStyles = { paddingTop: '6px', width: '50px', TextAlign: 'center', background: '#ffff' }
